import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
  CheckCircle,
  Globe,
  MapPin,
  MapTrifold,
  Star,
  X,
} from '@phosphor-icons/react';
import { db } from '../../../firebase';
import { CATEGORIES } from '../../../shared/constants/categories';
import { PriceLevelBadge } from '../../priceLevel';
import TagBadges from '../../tags/TagBadges';
import { safeExternalUrl } from '../utils/safeExternalUrl';
import PublicShareHeader from './Parts/PublicShareHeader';
import PublicShareLightbox from './Parts/PublicShareLightbox';

type PublicSharePageProps = {
  token: string;
  t: any;
};

export default function PublicSharePage({ token, t }: PublicSharePageProps) {
  const [share, setShare] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!db) {
        setLoading(false);
        return;
      }

      try {
        const snapshot = await getDoc(doc(db, 'publicShares', token));
        if (active && snapshot.exists()) setShare(snapshot.data());
      } catch {
        if (active) setShare(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (!share?.name) return;
    const previousTitle = document.title;
    document.title = `${share.name} · Dined`;
    return () => { document.title = previousTitle; };
  }, [share?.name]);

  if (loading) {
    return <main className="public-share-state"><img src="/dined-mark.svg" alt="" /><p>{t.loading}</p></main>;
  }

  if (!share) {
    return (
      <main className="public-share-state">
        <img src="/dined-mark.svg" alt="" />
        <h1>{t.publicShareUnavailable}</h1>
        <p>{t.publicShareUnavailableText}</p>
        <a className="primary" href="/">{t.openDined}</a>
      </main>
    );
  }

  const category = CATEGORIES[share.type as keyof typeof CATEGORIES];
  const CategoryIcon = category?.Icon;
  const gallery = [share.image, ...(share.images || [])].filter(Boolean)
    .filter((image, index, images) => images.indexOf(image) === index);
  const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(share.address)}`;
  const websiteUrl = safeExternalUrl(share.website || '');
  const openDinedUrl = `/?place=${encodeURIComponent(share.placeId)}`;
  const amenities = [
    { key: 'parking', visible: true },
    { key: 'degustation', visible: share.type === 'dining' },
    { key: 'familyFriendly', visible: share.type === 'entertainment' || share.type === 'event' },
  ].filter(({ visible }) => visible);

  return (
    <main className="public-share-page">
      <PublicShareHeader />
      <article className="public-share-card">
        {share.image && <img className="public-share-cover" src={share.image} alt="" />}
        <div className="public-share-content">
          <div className="public-share-title">
            <div>
              {CategoryIcon && <div className="detail-badge-row"><span className="detail-category" style={{ color: category.tone }}><CategoryIcon weight="fill" />{t[share.type]}</span><TagBadges tags={share.tags} tone={category.tone} /></div>}
              <h1>{share.name}</h1>
              <p><MapPin weight="fill" />{share.address}</p>
            </div>
            {Number(share.rating) > 0 && <span className="big-rating"><Star weight="fill" /><b>{Number(share.rating).toFixed(1)}</b></span>}
          </div>
          {share.description && <p className="public-share-description">{share.description}</p>}
          <div className="public-share-amenities">
            {share.type === 'dining' && <PriceLevelBadge value={share.priceLevel} t={t} />}
            {amenities.map(({ key }) => {
              const available = share[key] === true;
              const tooltipKey = `${key}${available ? 'AvailableHelp' : 'UnavailableHelp'}`;
              return <span className={`${available ? 'available' : 'unavailable'} amenity-tooltip`} data-tooltip={t[tooltipKey]} tabIndex={0} key={key}>{available ? <CheckCircle weight="fill" /> : <X weight="bold" />}{t[key]}</span>;
            })}
          </div>
          <div className="public-share-actions">
            <a className="primary" href={openDinedUrl}>{t.openDined}</a>
            <a className="secondary" href={directionsUrl} target="_blank" rel="noreferrer"><MapTrifold />{t.directions}</a>
            {websiteUrl && <a className="secondary public-share-icon-action" href={websiteUrl} target="_blank" rel="noreferrer" aria-label={t.visitWebsite} title={t.visitWebsite}><Globe /></a>}
          </div>
          {gallery.length > 1 && (
            <section className="public-share-gallery">
              <h2>{t.gallery}</h2>
              <div>{gallery.map((image, index) => <button type="button" onClick={() => setLightboxIndex(index)} key={image}><img src={image} alt={`${share.name} ${index + 1}`} /></button>)}</div>
            </section>
          )}
        </div>
      </article>
      {lightboxIndex !== null && <PublicShareLightbox images={gallery} initialIndex={lightboxIndex} onClose={() => setLightboxIndex(null)} t={t} />}
    </main>
  );
}
